/**
 * The portal's one door to the BuildCredits API.
 *
 * Every call goes to the same-origin proxy (/api/*) with the session token
 * from localStorage as a Bearer header; the API's envelope
 * { success, message, data } is unwrapped so call sites only see data, and a
 * failure becomes an ApiError carrying the HTTP status. A 401 on any route
 * other than the sign-in ones means the session is gone (signed out
 * elsewhere, expired, role changed): the token is dropped and the browser is
 * sent to the sign-in page with a `next` pointing back here.
 *
 * Retries: once, and only for GET/HEAD, on a network error or a 5xx. A
 * checkout or contribution POST is never retried - the API does not
 * de-duplicate those.
 */
export const TOKEN_KEY = 'bc_token';
export const SIGN_IN_PATH = '/sign-up-login-screen';

export interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T | null;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export interface SessionClaims {
  userId: string;
  email: string;
  roleCode: string;
  /** Unix seconds. */
  exp: number;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function getToken(): string | null {
  return storage()?.getItem(TOKEN_KEY) ?? null;
}

export function setToken(token: string | null): void {
  const s = storage();
  if (!s) return;
  if (token) s.setItem(TOKEN_KEY, token);
  else s.removeItem(TOKEN_KEY);
}

export function clearSession(): void {
  storage()?.removeItem(TOKEN_KEY);
}

/** The token's claims, for UI gating only - the API decides what is allowed. */
export function decodeClaims(token: string | null): SessionClaims | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1] ?? '';
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return {
      userId: String(json.userId ?? ''),
      email: String(json.email ?? ''),
      roleCode: String(json.roleCode ?? ''),
      exp: Number(json.exp ?? 0),
    };
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string | null): boolean {
  const claims = decodeClaims(token);
  return !claims || (claims.exp > 0 && claims.exp * 1000 <= Date.now());
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  timeoutMs?: number;
  /** Retry once on a network error / 5xx. Defaults to true for GET and HEAD, false otherwise. */
  retry?: boolean;
  /** Return the parsed JSON as-is (the gateway callback routes answer without the envelope). */
  raw?: boolean;
  signal?: AbortSignal;
}

const IDEMPOTENT = new Set(['GET', 'HEAD']);

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const retry = options.retry ?? IDEMPOTENT.has(method);
  const attempts = retry ? 2 : 1;
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await send<T>(path, method, options);
    } catch (err) {
      lastError = err;
      const retryable = err instanceof ApiError ? err.status === 0 || err.status >= 500 : false;
      if (!retryable || attempt === attempts) throw err;
      await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
    }
  }
  throw lastError;
}

async function send<T>(path: string, method: string, options: RequestOptions): Promise<T> {
  const headers = new Headers(options.headers ?? {});
  const token = getToken();
  if (token && !headers.has('authorization')) headers.set('authorization', `Bearer ${token}`);

  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    body = options.body;
  } else if (options.body !== undefined) {
    headers.set('content-type', 'application/json');
    body = JSON.stringify(options.body);
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? 15_000);
  options.signal?.addEventListener('abort', () => controller.abort(), { once: true });

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body,
      signal: controller.signal,
      cache: 'no-store',
    });
  } catch {
    throw new ApiError(
      controller.signal.aborted
        ? 'The request timed out. Please try again.'
        : 'The service is unreachable. Please check your connection and try again.',
      0
    );
  } finally {
    clearTimeout(timer);
  }

  const text = await res.text();
  let json: any = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }

  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    clearSession();
    redirectToSignIn('expired');
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }

  if (options.raw) {
    if (!res.ok) throw new ApiError(messageOf(json, res.status), res.status);
    return json as T;
  }

  if (json && typeof json === 'object' && 'success' in json) {
    const env = json as ApiEnvelope<T>;
    if (!res.ok || !env.success) throw new ApiError(messageOf(json, res.status), res.status);
    return env.data as T;
  }

  if (!res.ok) throw new ApiError(messageOf(json, res.status), res.status);
  return json as T;
}

function messageOf(json: any, status: number): string {
  const fromBody = json?.message || json?.error || json?.title;
  if (typeof fromBody === 'string' && fromBody.trim()) return fromBody;
  switch (status) {
    case 403:
      return 'You do not have access to this.';
    case 404:
      return 'Not found.';
    case 413:
      return 'The file is too large.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    default:
      return status >= 500
        ? 'Something went wrong on the server. Please try again.'
        : `Request failed (${status}).`;
  }
}

function redirectToSignIn(reason: string): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith(SIGN_IN_PATH)) return;
  const next = window.location.pathname + window.location.search;
  window.location.replace(`${SIGN_IN_PATH}?reason=${reason}&next=${encodeURIComponent(next)}`);
}

export const apiGet = <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
  api<T>(path, { ...options, method: 'GET' });

export const apiPost = <T>(
  path: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
) => api<T>(path, { ...options, method: 'POST', body });

export const apiPut = <T>(
  path: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
) => api<T>(path, { ...options, method: 'PUT', body });

export const apiDelete = <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
  api<T>(path, { ...options, method: 'DELETE' });

/** Multipart upload of one file under the given field name (the API reads "file"), plus any extra form fields. */
export function apiUpload<T>(
  path: string,
  file: File,
  field = 'file',
  extra: Record<string, string> = {}
): Promise<T> {
  const form = new FormData();
  form.append(field, file, file.name);
  for (const [k, v] of Object.entries(extra)) form.append(k, v);
  return api<T>(path, { method: 'POST', body: form, timeoutMs: 120_000, retry: false });
}

/** A protected file as a Blob (the token travels in the header, so a plain link would be refused). */
export async function apiBlob(path: string): Promise<Blob> {
  const headers = new Headers();
  const token = getToken();
  if (token) headers.set('authorization', `Bearer ${token}`);
  const res = await fetch(path, { headers, cache: 'no-store' });
  if (res.status === 401) {
    clearSession();
    redirectToSignIn('expired');
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }
  if (!res.ok) throw new ApiError(`Could not fetch the file (${res.status}).`, res.status);
  return res.blob();
}

export async function apiOpenFile(path: string): Promise<void> {
  const blob = await apiBlob(path);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Save a protected file to disk. The token travels in the header (a plain
 * <a href> would be refused), and the saved name comes from the server's
 * Content-Disposition when it sends one, else `fallbackName`.
 */
export async function apiDownload(path: string, fallbackName = 'download'): Promise<void> {
  const headers = new Headers();
  const token = getToken();
  if (token) headers.set('authorization', `Bearer ${token}`);
  const res = await fetch(path, { headers, cache: 'no-store' });
  if (res.status === 401) {
    clearSession();
    redirectToSignIn('expired');
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }
  if (!res.ok) throw new ApiError(`Could not download the file (${res.status}).`, res.status);
  const blob = await res.blob();
  const name = filenameFromDisposition(res.headers.get('content-disposition')) ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function filenameFromDisposition(value: string | null): string | null {
  if (!value) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(value);
  if (star) {
    try {
      return decodeURIComponent(star[1].replace(/"/g, '').trim());
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(value);
  return plain ? plain[1].trim() : null;
}
